import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useMemo } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Reservation } from '@/api';
import { useCustomer } from '@/api/customers/use-customer';
import { useAcceptAdditionalDriverRequest } from '@/api/reservations/use-accept-additional-driver-request';
import { useReservation } from '@/api/reservations/use-reservation';
import {
  findAdditionalDriver,
  isAdditionalDriverVerified,
} from '@/components/additional-drivers/status';
import BackButton from '@/components/back-button';
import { useModalStore } from '@/components/login-modal/store';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, showErrorMessage, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { PendingCheckmarkBubble } from '@/ui/icons/pending-checkmark-bubble';
import { Loader } from '@/ui/loader';

export default function AdditionalDrivers() {
  const { id, public_token } = useLocalSearchParams<{
    id: string;
    public_token?: string;
  }>();
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const status = useAuth.use.status();
  const loginModalMode = useModalStore((state) => state.loginModalMode);
  const setLoginModalMode = useModalStore((state) => state.setLoginModalMode);
  const [acceptFailed, setAcceptFailed] = React.useState(false);

  const [reservation, setReservation] = React.useState<Reservation | null>(
    null
  );

  const { data: resData, refetch } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: false,
  });

  const { data: customer } = useCustomer({ enabled: status === 'signIn' });

  const {
    mutateAsync: acceptAdditionalDriverInvite,
    isPending: isAcceptingAdditionalDriverInvite,
    isSuccess,
  } = useAcceptAdditionalDriverRequest();

  useEffect(() => {
    if (loginModalMode === 'none') {
      refetch();
    }
  }, [loginModalMode, refetch]);

  useEffect(() => {
    if (resData) {
      setReservation(resData);
    }
  }, [resData]);

  useEffect(() => {
    if (status === 'signOut') {
      setLoginModalMode('home');
      return;
    }

    if (status !== 'signIn' || isSuccess || acceptFailed) return;

    const acceptInvite = async () => {
      try {
        const res = await acceptAdditionalDriverInvite({
          id: Number(id),
          publicToken: public_token,
        });

        setReservation(res.data);
      } catch (error) {
        setAcceptFailed(true);
        showErrorMessage('Oops! Something went wrong. Please try again.');
      }
    };

    acceptInvite();
  }, [
    status,
    acceptFailed,
    isSuccess,
    acceptAdditionalDriverInvite,
    id,
    public_token,
    setLoginModalMode,
  ]);

  useEffect(() => {
    if (!isSuccess) return;

    mixpanelService.track('Page Viewed', {
      'Page Name': 'Invitation Accepted',
    });
  }, [isSuccess]);

  const additionalDriver = useMemo(() => {
    if (!reservation || !customer) return null;

    return findAdditionalDriver(reservation?.additionalDrivers, customer);
  }, [reservation, customer]);

  if (acceptFailed) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
          padding: 16,
          gap: 16,
        }}
      >
        <Text type="bodyRegular" style={{ textAlign: 'center' }}>
          We couldn't accept this invite.
        </Text>
        <Button
          text="Try Again"
          type="primary"
          onPress={() => {
            setAcceptFailed(false);
          }}
        />
      </View>
    );
  }

  if (!isSuccess || isAcceptingAdditionalDriverInvite) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  const isVerified = isAdditionalDriverVerified(additionalDriver);

  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        gap: 32,
        alignItems: 'center',
        backgroundColor: theme.colors.background_1,
      }}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS === 'web' && isDesktop() ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title: 'Invitation Accepted',
            headerTitle: 'Invitation Accepted',
            headerLeft: () => (
              <BackButton
                onPress={() => {
                  router.navigate('/trips');
                }}
              />
            ),
            headerShown: true,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
            headerTitleStyle: {
              fontSize: 17,
            },
          }}
        />
      )}

      <View style={styles.container}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.navigate('/trips');
              }}
            />

            <Text type="largeTitle">Invitation Accepted</Text>
          </View>
        ) : null}

        <View
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            gap: 16,
            flexGrow: 1,
          }}
        >
          <Icon
            type="naked"
            icon={<PendingCheckmarkBubble />}
            fillColor={theme.colors.success}
            width={40}
            height={40}
          />

          <View style={{ alignItems: 'center', gap: 8 }}>
            <Text type="title2" style={{ textAlign: 'center' }}>
              {isVerified ? "You're In!" : 'Almost There'}
            </Text>

            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
              }}
            >
              {isVerified
                ? 'Check in when your trip starts to use the car.'
                : 'Verify your details to join the trip.'}
            </Text>
          </View>
        </View>

        <View
          style={{
            alignItems: 'center',
            gap: 16,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            text={isVerified ? 'Go To Trip' : 'Start Verification'}
            type="primary"
            onPress={() => {
              if (isVerified) {
                router.navigate(`/trips/${id}/details`);
              } else {
                router.push(`/trips/${id}/additional-drivers/verify`);
              }
            }}
          />
        </View>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_1,
    padding: 16,
    gap: 16,
    justifyContent: 'space-between',
    width: {
      md: 600,
    },
    maxHeight: {
      md: 600,
    },
  },
}));
